import { useMemo, useState } from 'react';
import { Button, Divider, Select, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getErrorMessage } from '../utils/errorHandler';
import {
  useCreateDesignation,
  useFetchDesignationSelect,
} from '../Pages/designations/Hooks';

const normalizeName = (value) => String(value || '').trim().replace(/\s+/g, ' ');

export default function PositionSelect({
  operatorId,
  value,
  onChange,
  namespace = 'users',
  ...rest
}) {
  const { t } = useTranslation(namespace);
  const [search, setSearch] = useState('');
  const { data: designations = [] } = useFetchDesignationSelect({ operatorId });
  const { mutate: createDesignation, isPending } = useCreateDesignation({
    onSuccess: (response) => {
      const name = response?.data?.designation?.name;
      if (name) onChange?.(name);
      setSearch('');
      message.success(t('PositionCreated'));
    },
    onError: (error) => message.error(getErrorMessage(error, t('PositionCreateFailed'))),
  });

  const typed = normalizeName(search);
  const exists = designations.some(
    (item) => normalizeName(item.name).toLowerCase() === typed.toLowerCase(),
  );
  const canCreate = Boolean(operatorId) && typed.length > 0 && typed.length <= 80 && !exists;

  const options = useMemo(() => {
    const next = designations.map((item) => ({
      value: item.name,
      label: item.name,
    }));
    if (value && !next.some((item) => item.value === value)) {
      next.push({ value, label: value });
    }
    return next;
  }, [designations, value]);

  return (
    <Select
      {...rest}
      allowClear
      showSearch
      disabled={!operatorId}
      value={value || undefined}
      placeholder={t('SelectPosition')}
      options={options}
      optionFilterProp="label"
      onSearch={setSearch}
      onChange={(next) => {
        onChange?.(next ?? '');
        setSearch('');
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {canCreate ? (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Button
                type="text"
                block
                icon={<PlusOutlined />}
                loading={isPending}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => createDesignation({ operatorId, name: typed })}
              >
                {t('CreatePosition', { name: typed })}
              </Button>
            </>
          ) : null}
        </>
      )}
    />
  );
}

PositionSelect.propTypes = {
  operatorId: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  namespace: PropTypes.string,
};
